/**
 * HousingSystem.ts
 * 
 * Core Housing System Manager for Terra-Anima:
 * - Manages Land Ownership (Claims)
 * - Manages Placed Housing Objects (Floors, Walls, Furniture, Props, Exterior, Facilities)
 * - Persists and Loads Housing Data via localStorage (key: 'terra_anima_housing_data')
 * - Validates Placement Rights (Admin anywhere, Player only within their owned tiles)
 * - Handles Mailbox Tax & Letters (Red Mailbox integration)
 */

import {
  HousingTabType,
  PlacedHousingObject,
  LandOwnership,
  MailboxTaxData,
  LAND_TILE_PRICE_GOLD,
  LAND_TILE_PRICE_COPPER,
  HOUSING_PRESET_NPCS,
} from './HousingData';
import { housingCatalog } from './HousingCatalog';
import { Currency } from '../../core/Currency';
import { EntityState } from '../../core/Types';
import { EventBus } from '../../core/EventBus';

const HOUSING_STORAGE_KEY = 'terra_anima_housing_data_v1';

export class HousingSystem {
  private static instance: HousingSystem;

  // Lands mapped by `zoneId:x:y` -> LandOwnership
  private lands: Map<string, LandOwnership> = new Map();

  // Placed objects mapped by `zoneId:x:y` (or list)
  private placedObjects: Map<number, PlacedHousingObject[]> = new Map();

  // Mailboxes mapped by `zoneId:x:y` -> MailboxTaxData
  private mailboxes: Map<string, MailboxTaxData> = new Map();

  public static getInstance(): HousingSystem {
    if (!HousingSystem.instance) {
      HousingSystem.instance = new HousingSystem();
    }
    return HousingSystem.instance;
  }

  constructor() {
    this.loadFromStorage();
  }

  // ==========================================================================
  // 1. LAND OWNERSHIP (소유권 관리 및 매입)
  // ==========================================================================

  private makeLandKey(zoneId: number, x: number, y: number): string {
    return `${zoneId}:${Math.round(x)}:${Math.round(y)}`;
  }

  public getLandOwner(zoneId: number, x: number, y: number): LandOwnership | undefined {
    return this.lands.get(this.makeLandKey(zoneId, x, y));
  }

  public isOwnedBy(zoneId: number, x: number, y: number, userId: string): boolean {
    const land = this.getLandOwner(zoneId, x, y);
    if (!land) return false;
    return land.ownerId === userId;
  }

  /**
   * Check if player or admin can build at the specified coordinate.
   * Admin can build anywhere.
   * Regular player can only build inside their claimed land tiles.
   */
  public canBuildAt(zoneId: number, x: number, y: number, player: EntityState, isAdmin: boolean): boolean {
    if (isAdmin) return true;
    const land = this.getLandOwner(zoneId, Math.floor(x), Math.floor(y));
    return Boolean(land && land.ownerId === player.id);
  }

  /**
   * Claim a set of tiles.
   * If isAdmin, cost is 0G and can specify any target owner (e.g. King Solaris III).
   * If normal player, deducts 10G per tile from player's money.
   */
  public claimLandBatch(
    zoneId: number,
    tiles: Array<{ x: number; y: number }>,
    player: EntityState,
    isAdmin: boolean,
    targetOwnerId?: string,
    targetOwnerName?: string
  ): { success: boolean; message: string; totalGoldPaid?: number } {
    if (tiles.length === 0) {
      return { success: false, message: '선택된 타일이 없습니다.' };
    }

    const ownerId = isAdmin && targetOwnerId ? targetOwnerId : player.id;
    const ownerName = isAdmin && targetOwnerName ? targetOwnerName : player.name;

    // Check overlaps with other owners
    for (const t of tiles) {
      const existing = this.getLandOwner(zoneId, t.x, t.y);
      if (existing && existing.ownerId !== ownerId) {
        if (!isAdmin) {
          return {
            success: false,
            message: `(${t.x}, ${t.y}) 타일은 이미 [${existing.ownerName}] 님의 소유지입니다!`,
          };
        }
      }
    }

    // Payment calculation
    const totalTiles = tiles.length;
    const totalGold = isAdmin ? 0 : totalTiles * LAND_TILE_PRICE_GOLD;
    const totalCopper = Currency.toCopper({ gold: totalGold });

    if (!isAdmin) {
      const playerCopper = player.gold ?? 0;
      if (playerCopper < totalCopper) {
        return {
          success: false,
          message: `골드가 부족합니다! (필요: ${totalGold.toLocaleString()} Gold / 보유: ${Currency.breakdown(playerCopper).gold.toLocaleString()} Gold)`,
        };
      }
      player.gold = playerCopper - totalCopper;
    }

    // Register lands
    const now = Date.now();
    for (const t of tiles) {
      const key = this.makeLandKey(zoneId, t.x, t.y);
      this.lands.set(key, {
        x: t.x,
        y: t.y,
        zoneId,
        ownerId,
        ownerName,
        claimedAt: now,
        pricePaidGold: isAdmin ? 0 : LAND_TILE_PRICE_GOLD,
      });
    }

    this.saveToStorage();

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: isAdmin
        ? `👑 [관리자 토지 부여] (${totalTiles}칸) ➔ [${ownerName}] 님에게 무상 영지 지정 완료!`
        : `🏡 [소유권 등록 완료] 총 ${totalTiles}칸의 토지를 ${totalGold.toLocaleString()} Gold로 매입했습니다!`,
      category: 'system',
    });

    return {
      success: true,
      message: `${totalTiles}개 타일의 소유권이 등록되었습니다.`,
      totalGoldPaid: totalGold,
    };
  }

  // ==========================================================================
  // 2. PLACEMENT & DEMOLITION (오브젝트 배치 & 철거)
  // ==========================================================================

  public getPlacedObjectsInZone(zoneId: number): PlacedHousingObject[] {
    return this.placedObjects.get(zoneId) || [];
  }

  public placeObject(
    zoneId: number,
    catalogId: string,
    x: number,
    y: number,
    rotation: number,
    placedBy: string
  ): { success: boolean; message: string; object?: PlacedHousingObject } {
    const item = housingCatalog.get(catalogId);
    if (!item) {
      return { success: false, message: '유효하지 않은 하우징 자재입니다.' };
    }

    const currentList = this.getPlacedObjectsInZone(zoneId);

    // Overwrite or subgrid check
    // If placing floor or wall on exact same tile, replace existing
    const existingIndex = currentList.findIndex(
      (o) => Math.abs(o.x - x) < 0.2 && Math.abs(o.y - y) < 0.2 && o.tab === item.tab
    );

    const newObj: PlacedHousingObject = {
      id: `house_obj_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      catalogId: item.id,
      tab: item.tab,
      name: item.name,
      x,
      y,
      rotation,
      isObstacle: Boolean(item.isObstacle),
      placedBy,
      timestamp: Date.now(),
    };

    if (existingIndex >= 0) {
      currentList[existingIndex] = newObj;
    } else {
      currentList.push(newObj);
    }

    this.placedObjects.set(zoneId, currentList);

    // If red mailbox placed, ensure mailbox data structure exists
    if (catalogId === 'facil_red_mailbox') {
      const mbKey = this.makeLandKey(zoneId, Math.floor(x), Math.floor(y));
      if (!this.mailboxes.has(mbKey)) {
        this.mailboxes.set(mbKey, {
          depositedCopper: 0,
          lastTaxPaidAt: Date.now(),
          unpaidCycles: 0,
          letters: [
            {
              id: `welcome_letter_${Date.now()}`,
              sender: '👑 국왕 솔라리스 3세',
              title: '새 영지의 첫 건축을 축하하노라!',
              content: '그대의 땀과 열정으로 세운 이 집에 평화와 번영이 깃들기를 바란다. 세금 납부를 잊지 말도록 하라.',
              goldAttached: 100,
              receivedAt: Date.now(),
            },
          ],
        });
      }
    }

    this.saveToStorage();
    return { success: true, message: `[${item.name}] 설치 완료!`, object: newObj };
  }

  public demolishObjectAt(
    zoneId: number,
    x: number,
    y: number
  ): { success: boolean; message: string; demolished?: PlacedHousingObject } {
    const list = this.getPlacedObjectsInZone(zoneId);
    // Find closest placed object within 0.8 tiles
    let closestIndex = -1;
    let minDist = 0.85;

    for (let i = 0; i < list.length; i++) {
      const d = Math.hypot(list[i].x - x, list[i].y - y);
      if (d < minDist) {
        minDist = d;
        closestIndex = i;
      }
    }

    if (closestIndex >= 0) {
      const removed = list.splice(closestIndex, 1)[0];
      this.placedObjects.set(zoneId, list);
      this.saveToStorage();
      return { success: true, message: `[${removed.name}] 철거/회수 완료!`, demolished: removed };
    }

    return { success: false, message: '해당 위치에 철거할 하우징 오브젝트가 없습니다.' };
  }

  // ==========================================================================
  // 3. MAILBOX & TAX SYSTEM (우체통 & 세금 관리)
  // ==========================================================================

  public getMailboxAt(zoneId: number, x: number, y: number): MailboxTaxData | undefined {
    return this.mailboxes.get(this.makeLandKey(zoneId, Math.floor(x), Math.floor(y)));
  }

  public depositTaxToMailbox(
    zoneId: number,
    x: number,
    y: number,
    player: EntityState,
    copperAmount: number
  ): { success: boolean; message: string; currentBalanceCopper?: number } {
    const mb = this.getMailboxAt(zoneId, x, y);
    if (!mb) {
      return { success: false, message: '우체통 데이터를 찾을 수 없습니다.' };
    }

    const playerCopper = player.gold ?? 0;
    if (playerCopper < copperAmount) {
      return { success: false, message: '보유한 화폐가 부족합니다!' };
    }

    player.gold = playerCopper - copperAmount;
    mb.depositedCopper += copperAmount;
    this.saveToStorage();

    return {
      success: true,
      message: `우체통에 ${Currency.format(copperAmount)}를 예치했습니다.`,
      currentBalanceCopper: mb.depositedCopper,
    };
  }

  // ==========================================================================
  // 4. STORAGE PERSISTENCE
  // ==========================================================================

  private saveToStorage(): void {
    try {
      const serialized = {
        lands: Array.from(this.lands.entries()),
        placedObjects: Array.from(this.placedObjects.entries()),
        mailboxes: Array.from(this.mailboxes.entries()),
      };
      localStorage.setItem(HOUSING_STORAGE_KEY, JSON.stringify(serialized));
    } catch (e) {
      console.warn('[HousingSystem] Failed to save housing data to localStorage:', e);
    }
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(HOUSING_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.lands) {
        this.lands = new Map(parsed.lands);
      }
      if (parsed.placedObjects) {
        this.placedObjects = new Map(parsed.placedObjects);
      }
      if (parsed.mailboxes) {
        this.mailboxes = new Map(parsed.mailboxes);
      }
    } catch (e) {
      console.warn('[HousingSystem] Failed to parse saved housing data:', e);
    }
  }
}

export const housingSystem = HousingSystem.getInstance();
